import { computed, ref } from 'vue';
import type { Ref } from 'vue';
import { storeToRefs } from 'pinia';

import type { Experience, ExperienceWithFavorite } from '@/entities/experience';
import { useExperienceEntity } from '@/entities/experience/entity';
import type { ArticleResponse } from '@/shared/api/experience';
import { getBffV1ExperienceList } from '@/shared/api/experience';
import { getQueryParams } from '../lib/parseExperienceListQueryFromNextUrl';

export const useArticleExperiences = (data: Ref<ArticleResponse | null>) => {
  const isLoadingExperiences = ref(false);
  const experienceEntity = useExperienceEntity();
  const { favoritesState } = storeToRefs(experienceEntity);
  const extraResults = ref<Experience[]>([]);
  const count = ref<number>(0);
  /**
   * `undefined` — догрузку ещё не делали, смотрим `related_experiences.next` из статьи.
   * После ответа get_bff_v1_experience_list — только он: `string` есть ещё страницы, `null` — конец (не откатываемся к next статьи).
   */

  const nextPageUrl = computed(() => data.value?.related_experiences?.next);

  const displayedExperiences = computed<Experience[]>(() => {
    const initial = (data.value?.related_experiences?.results ?? []) as unknown as Experience[];
    return [...initial, ...extraResults.value];
  });

  const experiencesWithFavorite = computed<ExperienceWithFavorite[]>(() =>
    displayedExperiences.value.map((exp) => ({
      exp,
      favorite: favoritesState.value.some((favoriteItem) => favoriteItem.item_id === exp.id),
    })),
  );

  const showMoreButton = computed(() => {
    const totalCount = data.value?.related_experiences?.count;
    return totalCount != null && totalCount > experiencesWithFavorite.value.length && !!nextPageUrl.value;
  });

  const handleLoadMore = async () => {
    const nextUrl = nextPageUrl.value;
    if (!nextUrl || isLoadingExperiences.value) {
      return;
    }

    isLoadingExperiences.value = true;
    try {
      const response = await getBffV1ExperienceList({
        query: getQueryParams(nextUrl),
      });
      if (!response?.data) {
        return;
      }

      const batch = response.data.results as unknown as Experience[];
      extraResults.value = [...extraResults.value, ...batch];
      count.value = response.data.count;
    } finally {
      isLoadingExperiences.value = false;
    }
  };

  const handleSetFavorite = (id: number) => {
    experienceEntity.setFavorite(id);
  };

  const handleUnsetFavorite = (id: number) => {
    experienceEntity.unsetFavorite(id);
  };

  const handleCardClick = (_exp: Experience) => {};

  return {
    experiencesWithFavorite,
    isLoadingExperiences,
    showMoreButton,
    handleLoadMore,
    handleSetFavorite,
    handleUnsetFavorite,
    handleCardClick,
  };
};
